import jsPDF from "jspdf";
import brand_logo from "../../../assets/brand_logo4.png";
import dayjs from "dayjs";

const loadImageAsBase64 = async (url) => {
  try {
    if (!url) return null;

    const res = await fetch(url);
    if (!res.ok) return null;

    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Image load failed:", error);
    return null;
  }
};

const generateA4PDF = async ({
  bookIds = [],
  rooms = [],
  form = {},
  nights = 0,
  grandTotal = 0,
  paidAmount = 0,
  receivedAdvance = 0,
  remainingPayable = 0,
  discountAmount = 0,
  amountType = null,
  paymentStatus = "",
  loyaltyDiscountPercent = 0,
  loyaltyDiscountAmount = 0,
  gst = 0,
  gstType,
  gstPercent = 0,
  gstAmount = 0,
  headera4Img = "",
  footerImg = "",
  showUnitName = false,
  totalHours,
  bookingType,
  ratePerHour,
  overdue_amount = 0,
  overdue_duration = "",
  early_checkin_amount = 0,
  finalRemainingPayable,
}) => {
  const doc = new jsPDF("p", "mm", "a4");

  const pageW = 210;
  const pageH = 297;
  const m = 10;
  const cW = pageW - m * 2;

  const fitImageByWidth = (base64, maxW) =>
    new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        const ratio = img.height / img.width;
        resolve({
          base64,
          width: maxW,
          height: maxW * ratio,
        });
      };

      img.onerror = () =>
        resolve({
          base64,
          width: maxW,
          height: 20,
        });

      img.src = base64;
    });

  const C = {
    primary: [229, 231, 235],       // middle grey
    tableHeadBg: [229, 231, 235],   // table header grey
    accent: [0, 0, 0],
    accentLight: [248, 250, 252],
    success: [232, 245, 233],       // light green
    label: [0, 0, 0],
    text: [0, 0, 0],
    muted: [90, 90, 90],
    border: [220, 226, 232],
    cardBg: [250, 251, 253],
    white: [255, 255, 255],
    danger: [185, 28, 28],
  };

  const rgb = (color, type = "text") => {
    if (type === "text") doc.setTextColor(...color);
    if (type === "fill") doc.setFillColor(...color);
    if (type === "draw") doc.setDrawColor(...color);
  };

  const line = (x1, y1, x2, y2, color = C.border) => {
    rgb(color, "draw");
    doc.setLineWidth(0.18);
    doc.line(x1, y1, x2, y2);
  };

  const fmtRs = (v) =>
    `Rs. ${Math.round(Number(v || 0)).toLocaleString("en-IN")}`;

  const totalPayable = Math.round(Number(grandTotal || 0));
  const paid = Math.round(Number(paidAmount || 0));
  const discount = Math.round(Number(discountAmount || 0));
  const advance = Math.round(Number(receivedAdvance || 0));
  const remaining = Math.round(Number(remainingPayable || 0));
  const finalRemaining = Math.round(Number(finalRemainingPayable || 0));
  const overdueAmt = Math.round(Number(overdue_amount || 0));

  const isDiscount = amountType === "discount" || discount > 0;
  const isAdvance = amountType === "advance" || advance > 0 || remaining > 0;

  const isFullPaid = isDiscount
    ? paid + discount >= totalPayable
    : remaining <= 0 && paid >= totalPayable;

  const finalStatus =
    paymentStatus ||
    (isDiscount ? "Discount Applied" : isFullPaid ? "Fully Paid" : "Advance Received");

  const loyaltyPercent = Number(loyaltyDiscountPercent || 0);
  const loyaltyAmount = Math.round(Number(loyaltyDiscountAmount || 0));

  const finalGstPct = Number(gstPercent || gst || 0);

  const finalGstAmt =
    finalGstPct > 0
      ? Math.round(
        Number(
          gstAmount ||
          totalPayable - totalPayable / (1 + finalGstPct / 100)
        )
      )
      : 0;

  const rawHeader = await loadImageAsBase64(headera4Img);
  const rawFooter = await loadImageAsBase64(footerImg);

  const headerImage = rawHeader ? await fitImageByWidth(rawHeader, pageW) : null;
  const footerImage = rawFooter ? await fitImageByWidth(rawFooter, pageW) : null;

  const HDR = headerImage ? headerImage.height : 24;
  const FTR = footerImage ? footerImage.height : 12;

  let y = 0;

  const addFooter = () => {
    const fy = pageH - FTR;

    if (footerImage) {
      doc.addImage(
        footerImage.base64,
        "PNG",
        0,
        fy,
        footerImage.width,
        footerImage.height
      );
      return;
    }

    rgb(C.primary, "fill");
    doc.rect(0, fy, pageW, FTR, "F");

    rgb(C.text, "text");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("Thank you for your stay. Visit again!", pageW / 2, fy + 7, {
      align: "center",
    });
  };

  const addHeader = () => {
    if (headerImage) {
      doc.addImage(
        headerImage.base64,
        "PNG",
        0,
        0,
        headerImage.width,
        headerImage.height
      );
      y = HDR + 3;
    } else {
      rgb(C.primary, "fill");
      doc.rect(0, 0, pageW, HDR, "F");

      try {
        doc.addImage(brand_logo, "PNG", 10, 5, 26, 12);
      } catch { }

      rgb(C.text, "text");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("TAX INVOICE", pageW / 2, 10, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text("Booking Receipt", pageW / 2, 16, { align: "center" });

      y = HDR + 3;
    }

    if (showUnitName) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      rgb(C.muted, "text");
      doc.text("Unit of Sri Chakra Enterprises", 23, y - 5, {
        align: "center",
      });
    }

    line(m, y, pageW - m, y);
    y += 4;
  };

  const checkPage = (needed = 20) => {
    if (y + needed > pageH - FTR - 8) {
      addFooter();
      doc.addPage();
      addHeader();
    }
  };

  const sectionTitle = (title) => {
    checkPage(10);

    rgb(C.cardBg, "fill");
    doc.roundedRect(m, y, cW, 7, 1.5, 1.5, "F");

    rgb(C.border, "draw");
    doc.roundedRect(m, y, cW, 7, 1.5, 1.5);

    rgb(C.text, "text");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(String(title).toUpperCase(), m + 3, y + 4.8);

    y += 10;
  };

  const smallPair = (x, label, value, width = 85) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    rgb(C.label, "text");
    doc.text(`${String(label)}:`, x, y);

    doc.setFont("helvetica", "normal");
    rgb(C.text, "text");

    const lines = doc.splitTextToSize(String(value ?? "—"), width - 26);
    doc.text(lines, x + 25, y);

    return Math.max(5, lines.length * 4.2);
  };

  addHeader();

  if (bookIds?.[0]) {
    checkPage(13);

    rgb(C.accentLight, "fill");
    doc.roundedRect(m, y, cW, 9, 1.5, 1.5, "F");

    rgb(C.border, "draw");
    doc.roundedRect(m, y, cW, 9, 1.5, 1.5);

    rgb(C.accent, "text");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text("Booking Ref:", m + 3, y + 5.8);

    doc.setFont("courier", "bold");
    doc.text(String(bookIds[0]), m + 26, y + 5.8);

    const badgeW = 42;
    const badgeX = pageW - m - badgeW;

    y += 13;
  }

  const startY = y;
  const colL = m;
  const colR = m + cW / 2 + 4;
  const colW = cW / 2 - 6;

  sectionTitle("Guest Details");

  const guestStartY = y;

  const guestPairs = [
    ["Name", form?.guest_name || "—"],
    ["Phone", form?.phone || "—"],
    ["Email", form?.email || "—"],
    ["GST No.", form?.customer_gst || "—"],
    ["ID Type", form?.id_type || "—"],
    ["ID No.", form?.id_number || "—"],
  ];

  if (form?.address) guestPairs.push(["Address", form.address]);

  guestPairs.forEach(([label, value]) => {
    const h = smallPair(colL + 1, label, value, colW);
    y += h;
  });

  const afterGuestY = y;

  y = startY;

  sectionTitle("Stay Details");

  const stayPairs = [
    ["Booking Type", bookingType || "—"],
    [
      "Check-In",
      `${form?.check_in ? dayjs(form.check_in).format("DD-MM-YYYY") : "—"}${form?.check_in_time ? ` (${form.check_in_time})` : ""
      }`,
    ],
    [
      "Check-Out",
      `${form?.check_out ? dayjs(form.check_out).format("DD-MM-YYYY") : "—"}${form?.check_out_time ? ` (${form.check_out_time})` : ""
      }`,
    ],
    ["Nights", `${nights || 0} night${Number(nights) === 1 ? "" : "s"}`],
    ["Guests", form?.guests || "—"],
    ["Pay Mode", form?.payment_mode || "—"],
  ];

  //   if (totalHours) stayPairs.push(["Total Hours", totalHours]);
  //   if (ratePerHour) stayPairs.push(["Rate Per Hour", fmtRs(ratePerHour)]);

  stayPairs.forEach(([label, value]) => {
    const h = smallPair(colR + 1, label, value, colW);
    y += h;
  });

  const afterStayY = y;

  y = Math.max(afterGuestY, afterStayY);

  line(pageW / 2, guestStartY - 10, pageW / 2, y + 1);
  y += 4;
  line(m, y, pageW - m, y);
  y += 4;

  sectionTitle(`Room Details (${rooms.length} ${rooms.length === 1 ? "Room" : "Rooms"})`);

  const cols = [
    { title: "Room", w: 42 },
    { title: "Type", w: 28 },
    { title: "Rate", w: 27 },
    { title: "AC", w: 22 },
    { title: "Extra", w: 31 },
    { title: "Total", w: 40 },
  ];

  let startX = m;

  rgb(C.tableHeadBg, "fill");
  doc.roundedRect(m, y, cW, 8, 1.5, 1.5, "F");

  rgb(C.text, "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);

  cols.forEach((col) => {
    doc.text(col.title, startX + 2, y + 5);
    startX += col.w;
  });

  y += 8;

  rooms.forEach((roomItem, idx) => {
    checkPage(11);

    const pricePerDay = Number(roomItem?.price_per_day || 0);
    const maxOccupancy = Number(roomItem?.max_occupancy || 0);
    const addCost = Number(roomItem?.add_cost_per_occupancy || 0);
    const acCharge = Number(roomItem?.ac_charge || 0);
    const guests = Number(form?.guests || 1);
    const totalNights = Number(nights || 0);

    const extraGuests = Math.max(0, guests - maxOccupancy);
    const extraTotal = extraGuests * addCost * totalNights;

    const roomTotal =
      pricePerDay * totalNights + extraTotal + acCharge * totalNights;

    const row = [
      `${roomItem?.room_name || "Room"} #${roomItem?.room_number || "—"}`,
      roomItem?.room_type || "—",
      fmtRs(pricePerDay),
      fmtRs(acCharge),
      fmtRs(extraTotal),
      fmtRs(roomTotal),
    ];

    rgb(idx % 2 === 0 ? C.cardBg : C.white, "fill");
    doc.rect(m, y, cW, 8, "F");

    rgb(C.border, "draw");
    doc.rect(m, y, cW, 8);

    rgb(C.text, "text");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.7);

    let x = m;

    row.forEach((cell, i) => {
      const text = doc.splitTextToSize(String(cell), cols[i].w - 4);
      doc.text(text[0], x + 2, y + 5);
      x += cols[i].w;
    });

    y += 8;
  });

  y += 5;

  sectionTitle("Payment Summary");

  const payItems = [];

  if (loyaltyPercent > 0) {
    payItems.push([
      `Loyalty Discount (${loyaltyPercent}%)`,
      `- ${fmtRs(loyaltyAmount)}`,
    ]);
  }

  if (Number(early_checkin_amount) > 0) {
    payItems.push(["Early Check-In Amount", fmtRs(early_checkin_amount)]);
  }

  if (overdueAmt > 0) {
    payItems.push([
      `Overdue Amount${overdue_duration ? ` (${overdue_duration})` : ""}`,
      fmtRs(overdueAmt),
    ]);
  }

  if (gstType === "inclusive") {
    payItems.push(["Total Payable Amount", fmtRs(totalPayable + overdueAmt)]);

    if (finalGstPct > 0) {
      const half = finalGstAmt / 2;
      payItems.push([`CGST (${finalGstPct / 2}%)`, fmtRs(half)]);
      payItems.push([`SGST (${finalGstPct / 2}%)`, fmtRs(half)]);
    }
  } else if (gstType === "exclusive") {
    payItems.push([
      "Total Payable Amount",
      fmtRs(totalPayable + overdueAmt),
    ]);

    if (finalGstPct > 0) {
      payItems.push([`GST (${finalGstPct}%) Included`, ""]);
    }
  } else {
    payItems.push(["Total Payable Amount", fmtRs(totalPayable + overdueAmt)]);
  }

  payItems.push(["Amount Paid", fmtRs(paid)]);

  if (isDiscount) {
    payItems.push(["Discount Applied", `- ${fmtRs(discount)}`]);
  }

  if (isAdvance && !isDiscount) {
    // payItems.push(["Advance Received", fmtRs(advance || paid)]);
    payItems.push(["Remaining Payable", fmtRs(finalRemaining)]);
  }

  checkPage(payItems.length * 7 + 20);

  const paymentBoxH = payItems.length * 7 + 8;

  rgb(C.cardBg, "fill");
  doc.roundedRect(m, y - 3, cW, paymentBoxH, 1.5, 1.5, "F");

  rgb(C.border, "draw");
  doc.roundedRect(m, y - 3, cW, paymentBoxH, 1.5, 1.5);

  payItems.forEach(([lbl, val], i) => {
    const rowY = y + i * 7;

    doc.setFont("helvetica", val ? "bold" : "normal");
    doc.setFontSize(val ? 7.2 : 6.5);
    rgb(val ? C.label : C.muted, "text");
    doc.text(lbl, m + 4, rowY + 2);

    if (val) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      rgb(C.text, "text");
      doc.text(val, pageW - m - 4, rowY + 2, {
        align: "right",
      });
    }

    if (i < payItems.length - 1) {
      line(m + 4, rowY + 4.5, pageW - m - 4, rowY + 4.5, C.border);
    }
  });

  y += paymentBoxH + 4;

  checkPage(18);

  const grandTotalAmount = totalPayable + overdueAmt - discount;

  rgb(C.success, "fill");
  doc.roundedRect(m, y, cW, 13, 2, 2, "F");

  rgb(C.text, "text");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("GRAND TOTAL", m + 4, y + 8);

  doc.setFontSize(12);
  doc.text(fmtRs(grandTotalAmount), pageW - m - 4, y + 8, {
    align: "right",
  });

  y += 17;


  addFooter();

  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);

  window.open(pdfUrl, "_blank");

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = pdfUrl;

  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow.focus();

    setTimeout(() => {
      iframe.contentWindow.print();
    }, 500);

    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(pdfUrl);
    }, 5000);
  };
};

export default generateA4PDF;